import React, { useEffect, useState, useRef, useCallback } from "react";
import style from "./company.module.scss";
import Layout from "components/layout/Layout";
import { Filter } from "components/UI/filter/filter";
import { SearchInput } from "components/UI/searchInput/searchInput";
import { CompanyTableRow } from "components/companyTableRow/companyTableRow";
import { Button } from "components/UI/button/button";
import { BackArrow } from "components/UI/backArrow/backArrow";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { RoutePaths } from "app/routing/routing";
import { Modal } from "components/modal/modal";
import { ApplicationFormFields, NewApplication } from "components/newApplicationForm/newApplicationForm";
import { createApplicationRequest, useGetApplications } from "actions/applicationsActions";
import NoDataMessage from "components/noDataBlock/noDataMessage";
import { IntStatus } from "utils/commonFunctions";
import { LoadingContainer } from "components/UI/loadingContainer/loadingContainer";
import SingleFilterDropDown, { OptionItem } from "components/UI/singleFilterDropDown/singleFilterDropDown";
import { Client, getClientById } from "actions/clientActions";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { Company as CompanyType, getClient } from "actions/companyActions";
import translations from "locales";

const intStatusOptions = [
  { id: '0', label: 'Active' },
  { id: '2', label: 'Closed' },
  { id: '3', label: 'On Hold' },
  { id: '4', label: 'Pending Review' },
  { id: '1', label: 'Submitted' },
];

const sortOptions: OptionItem[] = [
  {
    label: "Matter Number - Highest to Lowest",
    value: { orderBy: "fileNo", sortOrder: "DESC" },
  },
  {
    label: "Matter Number - Lowest to Highest",
    value: { orderBy: "fileNo", sortOrder: "ASC" },
  },
  {
    label: "Applicant Name - Alphabetical (A to Z)",
    value: { orderBy: "ApplicantName", sortOrder: "ASC" },
  },
  {
    label: "Applicant Name - Alphabetical (Z to A)",
    value: { orderBy: "ApplicantName", sortOrder: "DESC" },
  },
  {
    label: "Status - Alphabetical (A to Z)",
    value: { orderBy: "intStatus", sortOrder: "ASC" },
  },
  {
    label: "Status - Alphabetical (Z to A)",
    value: { orderBy: "intStatus", sortOrder: "DESC" },
  },
];


export const Company = () => {
  const history = useHistory();
  const location = useLocation<{ company?: CompanyType }>();
  const { companyId } = useParams<{ companyId: string }>();

  const clientID = companyId;
  if (!clientID) {
    history.push(RoutePaths.Dashboard);
  }
  const [canAddNewMatter, setCanAddNewMatter] = useState<boolean>(false);
  const [searchWord, setSearchWord] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [intStatus, setIntStatus] = useState<Array<IntStatus>>([]);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [isFilterDropdownVisible, setFilterDropdownVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyType | undefined>(
    location.state?.company
  );
  const lang = localStorage.getItem('lang') || 'en';

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!company) {
        try {
          const company = await getClient(clientID);
          setCompany(company);
        } catch (error) {
          console.error("Error fetching client data: ", error);
        }
      }
    };
    const fetchClientData = async () => {
      try {
        const clientData = await getClientById({ clientId: clientID });
        setCanAddNewMatter(clientData.bitButtonVisible == 1);
      } catch (error) {
        console.error("Error fetching client data: ", error);
      }
    };
    if (!clientID) return;
    fetchClientData();
    fetchCompanyData();
  }, [clientID, company]);

  const {
    data: applicationsData,
    error: applicationsError,
    mutate: mutateApplications,
    noDataFound: noApplicationsDataFound,
    isValidating,
    size,
    setSize,
    hasMore,
  } = useGetApplications({
    rowNumber: 100,
    searchWord: searchWord,
    clientID: clientID,
    orderBy: orderBy,
    sortOrder: sortOrder,
    intStatus: intStatus,
  });



  const handleSortChange = (value: any) => {
    setOrderBy(value?.orderBy || "");
    setSortOrder(value?.sortOrder || "");
    resetData();
  };

  const onSearch = (value: string) => {
    setSearchWord(value);
    resetData();
  };

  const onFilterChange = async (selectedOptions: any[]) => {
    setIntStatus(selectedOptions as IntStatus[]);
    resetData();
  };

  const onViewClick = (
    fileId: string,
    fileData: { fileNo: string; fileDesc: string }
  ) => {
    history.push({
      pathname: `${RoutePaths.Company}/${clientID}${RoutePaths.Matter}/${fileId}`,
      state: { 
        fileDesc: fileData.fileDesc,
        fileNo: fileData.fileNo 
      }
    });
  };


  useEffect(() => {
    if (showApplicationModal) {
      setIsSubmitting(false);
      setSubmissionError(null);
    }
  }, [showApplicationModal]);

  const onClickNewApplication = () => {
    setShowApplicationModal(true);
  };

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setShowApplicationModal(false);
    }
  };

  const onSubmitApplication = async (data: ApplicationFormFields) => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      
      await createApplicationRequest({
        clientID: clientID,
        formFields: data,
        reviewStatus: IntStatus.PendingReview,
        fileUploads: data.fileUploads,
      });
      
      handleCloseModal();
      mutateApplications();
    } catch (error: any) {
      console.error("Error submitting application: ", error);
      let errorMessage = "There was an error submitting your application. Please try again.";
      
      // Handle different types of errors
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.message && data.message.includes("Missing required parameters")) {
            errorMessage = "Your application is missing required information. Please check all fields and try again.";
          } else {
            errorMessage = data.message || errorMessage;
          }
        } else if (status === 500) {
          // Handle server errors
          errorMessage = data.message || "There was a server error. Please try again later.";
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message || errorMessage;
      }
      
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetData = () => {
    setSize(1);
    mutateApplications();
  };

  const loadMore = () => {
    setSize(prevSize => prevSize + 1);
  };

  const [loader] = useInfiniteScroll(loadMore, hasMore, [orderBy, sortOrder, searchWord, intStatus]);

  return (
    <Layout>
      <div className={style.company}>
        <div className={style.backArrow}>
          <BackArrow to={RoutePaths.Dashboard}/>
        </div>
        <h2 className={style.headerDesktop}>
          {location.state?.company?.clNo || company?.clNo || ''} - {location.state?.company?.clName || company?.clName || ''}’s Matters
        </h2>
        <h2 className={style.headerMobile}>{location.state?.company?.clName || company?.clName || '' }’s Matters</h2>
      </div>
      <div className={style.filterButtons}>
        <Filter
          id={"filterMatters"}
          text={lang === 'fr' ? translations.fr.filter_matters : translations.en.filter_matters}
          isVisible={isFilterDropdownVisible}
          setVisible={setFilterDropdownVisible}
          onCheckboxChange={onFilterChange}
          filterOptions={lang === 'fr' ? translations.fr.intStatusOptionsFr : intStatusOptions}
          mobileText={lang === 'fr' ? translations.fr.filter : translations.en.filter}
          className={style.filter}
        />
        <SingleFilterDropDown
          id={'sortMatters'}
          label={lang === 'fr' ? translations.fr.sort_matters : translations.en.sort_matters}
          optionItems={lang === 'fr' ? translations.fr.sortOptionsFrCompany : sortOptions}
          onOptionSelected={handleSortChange}
          dropDownDirectionClass={style.dropDownDirectionClass}
          className={style.sortMatters}
          mobileText={lang === 'fr' ? translations.fr.sort : translations.en.sort}
        />
        <SearchInput
          id={"searchMatters"}
          placeholder={lang === 'fr' ? translations.fr.try_work_permit : translations.en.try_work_permit}
          onChange={onSearch}
          className={style.searchBar}
        />
        {canAddNewMatter && (
          <Button
            id="newApplicationButton"
            onClick={onClickNewApplication}
            style="upload"
            mobileText="New"
            className={style.newApplicationButton}
          >
            {lang === 'fr' ? translations.fr.new_matter : translations.en.new_matter}
          </Button>
        )}
      </div>
      <div className={style.clientTableContainerWrapper}>
        <div className={style.clientTableContainer}>
          <div>
            <table className={style.clientTable}>
              <thead>
                <tr>
                  <th>{lang === 'fr' ? translations.fr.matter_number : translations.en.matter_number}</th>
                  <th>{lang === 'fr' ? translations.fr.applicant_name : translations.en.applicant_name}</th>
                  <th className={style.description}>{lang === 'fr' ? translations.fr.description : translations.en.description}</th>
                  <th className={style.description}>{lang === 'fr' ? translations.fr.lawyer : translations.en.lawyer}</th>
                  <th className={style.description}>{lang === 'fr' ? translations.fr.created_on : translations.en.created_on}</th>
                  <th>{lang === 'fr' ? translations.fr.status : translations.en.status}</th>
                </tr>
              </thead>
              <tbody>
                {applicationsData &&
                  applicationsData.filter(application => application !== null).map((applicationRowData, index) => {
                    return (
                      <CompanyTableRow
                        key={index}
                        number={applicationRowData.fileNo}
                        company={applicationRowData.applicantName}
                        description={applicationRowData.fileDesc}
                        status={applicationRowData.fileStatus}
                        lawyer={applicationRowData.lawyer}
                        createdOn={applicationRowData.createdOn}
                        onClick={() =>
                          onViewClick(
                            applicationRowData.fileId,
                            {
                              fileNo: applicationRowData.fileNo.toString(),
                              fileDesc: applicationRowData.fileDesc
                            }
                          )
                        }
                      />
                    );
                  })}
                {isValidating && applicationsData && size > 2 && <tr><td><span>Loading...</span></td></tr>}
              </tbody>
            </table>
            <div ref={loader} style={{ height: '1px', backgroundColor: 'transparent' }} />
            {size <= 2 && isValidating &&  <LoadingContainer loadingLabel="Loading matters..." />}
          </div>
          {noApplicationsDataFound && !isValidating && (
            <NoDataMessage
              title="No Matters Found!"
              description="Please reach out to your primary contact at the firm for assistance."
            />
          )}
        </div>
      </div>

      <Modal
        id="newApplicationModal"
        title={lang === 'fr' ? translations.fr.new_matter : translations.en.new_matter}
        show={showApplicationModal}
        onClose={handleCloseModal}
        className={style.newApplication}
        headerClass={style.newApplicationHeader}
        hideModalHeader={isSubmitting}
      >
        {isSubmitting ? (
          <LoadingContainer loadingLabel="Your request is being submitted" />
        ) : (
          <>
            {submissionError && (
              <div style={{ 
                color: "red", 
                backgroundColor: "#ffeeee", 
                padding: "10px", 
                marginBottom: "15px", 
                borderRadius: "4px",
                border: "1px solid #ffcccc"
              }}>
                {submissionError}
              </div>
            )}
            <NewApplication onSubmitForm={onSubmitApplication} />
          </>
        )}
      </Modal>
    </Layout>
  );
};
